import { CheckCircle2, FileText, Trash2, Upload } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { cn } from '@/lib/utils'
import { useDocumentKinds, usePersonnelFile } from '../api'
import { SectionCard } from '../components/SectionCard'
import { rec, rowsOf, str } from '../map'
import type { PersonelFormMode, PersonelFormValues } from '../schemas/personelFormSchema'

const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp'

export function BelgeFormTab({ mode }: { mode: PersonelFormMode }) {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const { control, getValues, setValue, formState } = useFormContext<PersonelFormValues>()
  const kinds = useDocumentKinds().data ?? []
  const file = usePersonnelFile(id, mode === 'edit')
  const belgeler = useWatch({ control, name: 'belgeler' }) ?? []
  const docError = formState.errors.belgeler?.message

  /** Düzenlemede zaten yüklü belgeler (tür → dosya adı). */
  const uploaded = useMemo(() => {
    const map = new Map<string, string>()
    for (const d of rowsOf(rec(file.data).documents).map(rec)) map.set(str(d.kind_key), str(d.original_name) || str(d.title))
    return map
  }, [file.data])

  // Form satırları BE'deki tür listesinden oluşur.
  useEffect(() => {
    if (!kinds.length) return
    const current = getValues('belgeler') ?? []
    if (kinds.every((k) => current.some((b) => b.tip === k.key))) return
    setValue(
      'belgeler',
      kinds.map((k) => current.find((b) => b.tip === k.key) ?? { tip: k.key, dosya_adi: '', dosya: null }),
    )
  }, [kinds, getValues, setValue])

  const setFile = (tip: string, f: File | null) => {
    const current = getValues('belgeler') ?? []
    setValue(
      'belgeler',
      current.map((b) => (b.tip === tip ? { tip, dosya_adi: f?.name ?? '', dosya: f } : b)),
      { shouldValidate: formState.isSubmitted, shouldDirty: true },
    )
  }

  const remove = async (tip: string, name: string) => {
    const ok = await confirmModal({
      title: t('confirm.uploadRemove.title'),
      description: t('confirm.uploadRemove.body', { name }),
      confirmLabel: t('hr.personel.form.removeDoc'),
      destructive: true,
    })
    if (ok) setFile(tip, null)
  }

  return (
    <SectionCard title={t('hr.personel.form.sections.belgeler')} icon={<FileText />} description={t('hr.personel.form.hints.belgeler')}>
      {docError ? <p className="mb-3 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{String(docError)}</p> : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {kinds.map((def) => {
          const picked = belgeler.find((b) => b.tip === def.key)?.dosya_adi ?? ''
          const existing = uploaded.get(def.key) ?? ''
          const shown = picked || existing
          return (
            <div
              key={def.key}
              className={cn(
                'flex min-w-0 flex-col gap-3 rounded-xl border bg-card p-3.5 min-[480px]:flex-row min-[480px]:items-center min-[480px]:justify-between sm:flex-col sm:items-stretch lg:flex-row lg:items-center',
                shown ? 'border-success/30' : def.required && mode === 'create' ? 'border-warning/40' : 'border-border/70',
              )}
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', shown ? 'bg-success/12 text-success' : 'bg-muted text-muted-foreground')}>
                  {shown ? <CheckCircle2 className="size-4" /> : <FileText className="size-4" />}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {def.title}
                    {def.required ? <span className="ml-1 text-destructive">*</span> : null}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{shown || t('hr.personel.form.docEmpty')}</div>
                </div>
              </div>
              <div className="flex shrink-0 items-center justify-end gap-1">
                {picked ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 text-muted-foreground hover:text-destructive"
                    aria-label={t('hr.personel.form.removeDoc')}
                    onClick={() => void remove(def.key, picked)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                ) : null}
                <label className="inline-flex h-9 flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-input bg-card px-3 text-xs font-medium hover:bg-muted min-[480px]:h-8 min-[480px]:flex-none sm:h-9 sm:flex-1 lg:h-8 lg:flex-none">
                  <input
                    type="file"
                    accept={ACCEPT}
                    className="sr-only"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) setFile(def.key, f)
                      e.target.value = ''
                    }}
                  />
                  <Upload className="size-4" />
                  {shown ? t('hr.personel.form.replaceDoc') : t('hr.personel.form.uploadDoc')}
                </label>
              </div>
            </div>
          )
        })}
      </div>
    </SectionCard>
  )
}
